<template>
    <template v-if="blockHoisted">
        <header
            class="tl-navigation tl-navigation-mobile lg-hidden"
            :class="{ expanded }"
        >
            <button
                type="button"
                class="tl-navigation-toggle"
                aria-label="Menu"
                :aria-expanded="expanded"
                @click="toggle"
            >
                <span></span>
                <span></span>
                <span></span>
            </button>

            <div class="tl-navigation-brand">
                {{ mobileHeading }}
            </div>

            <nav class="tl-navigation-overlay">
                <SelectionList
                    v-for="(group, idx) in itemGroups"
                    :key="idx"
                    :horizontal="horizontal"
                    :items="group.items"
                    :loading="loading"
                    :custom-css-variables="customCssVariables"
                    @item-chosen="(item) => $emit('itemChosen', item)"
                />
            </nav>
        </header>
    </template>
    <nav
        v-else
        :id="id"
        class="tl-navigation sm-hidden"
    >
        <SelectionList
            v-for="(group, idx) in itemGroups"
            :key="idx"
            :horizontal="horizontal"
            :items="group.items"
            :loading="loading"
            :custom-css-variables="customCssVariables"
            @item-chosen="(item) => $emit('itemChosen', item)"
        />
    </nav>
</template>

<script setup lang="ts">
import type { NavigationItemGroup, SelectionListItem } from 'widget-components/integrations/CustomPage/types'
import type { CustomCssVariables } from 'widget-components/types'
import SelectionList from 'widget-components/components/SelectionList/SelectionList.vue'
import { ref } from 'vue'

defineOptions({ inheritAttrs: false })

defineProps<{
    id?: string
    horizontal?: boolean
    itemGroups: NavigationItemGroup[] // TODO handle multiple groups w/accordion
    loading?: boolean
    blockHoisted?: boolean
    mobileHeading?: string
    customCssVariables?: CustomCssVariables
}>()

defineEmits<{
    (e: 'itemChosen', item: SelectionListItem): void
}>()

const expanded = ref(false)

const handleEscKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        event.stopPropagation()
        toggle()
    }
}

const toggle = () => {
    expanded.value = !expanded.value

    if (expanded.value) {
        document.addEventListener('keydown', handleEscKey, { capture: true })
    } else {
        document.removeEventListener('keydown', handleEscKey, { capture: true })
    }
}

</script>

<style lang="sass" scoped>

$z-index-base: 50
$navbar-height: 56px

.tl-navigation
    &-mobile
        position: sticky
        top: 0
        z-index: $z-index-base
        display: flex
        gap: 8px
        align-items: center
        height: $navbar-height
        padding: 0 16px 0 8px
        background: var(--tl-navigation-mobile-bar-bgcolor, var(--tl-widget-background-color))
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)

        &.expanded
            z-index: calc(var(--layer-widget-close-button, $z-index-base) + 1)

    &-brand
        font-size: 1.2rem
        font-weight: bold
        overflow: hidden
        text-overflow: ellipsis

    &-toggle
        position: relative
        width: 40px
        height: 40px
        padding: 8px
        border: none
        background: none
        border-radius: 50%
        cursor: pointer
        overflow: visible
        z-index: $z-index-base + 2
        flex-shrink: 0

        span
            position: absolute
            left: 50%
            width: 24px
            height: 2px
            background: var(--tl-container-text-primary-color, var(--color-black))
            border-radius: 2px
            transform-origin: center
            transition: transform 0.28s cubic-bezier(.2,.8,.2,1), opacity 0.2s
            transform: translateX(-50%) translateY(0)

            &:nth-child(1)
                top: calc(50% - 8px)
            &:nth-child(2)
                top: 50%
            &:nth-child(3)
                top: calc(50% + 8px)

        .expanded > &
            background: var(--tl-navigation-mobile-overlay-bgcolor, var(--tl-widget-background-color))

            span:nth-child(1)
                transform: translateX(-50%) translateY(8px) rotate(45deg)
            span:nth-child(2)
                opacity: 0
                transform: translateX(-50%) scaleX(0)
            span:nth-child(3)
                transform: translateX(-50%) translateY(-8px) rotate(-45deg)

    &-overlay
        position: absolute
        inset: 0
        height: 100vh
        z-index: $z-index-base - 1
        overflow: auto
        background: var(--tl-navigation-mobile-overlay-bgcolor, var(--tl-widget-background-color))
        padding: 56px 16px
        gap: 1.5rem
        font-size: 1.5rem
        opacity: 0
        pointer-events: none
        transition: opacity var(--tl-transition-duration) ease

        .expanded > &
            opacity: 1
            pointer-events: all
</style>
